import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { SDKConnected } from 'features/sdk/sdkStateSlice';
import { selectRecorderLogoVisibility } from 'features/recorder/recorderSlice';
import { selectRestreamerLogoVisibility } from 'features/restreamer/restreamerSlice';
import { selectCurrentSystemRole } from 'features/application/selectors';
import { TopbarState } from 'features/topbar/types';
import { selectAllowChangeLogo } from 'features/branding/brandingSlice';
import { styleConstants } from 'theme/styleConstants';

export const initialState: TopbarState = {
  visible: true,
  enabled: false,
};

export const topbarSlice = createSlice({
  name: 'topbar',
  initialState,
  reducers: {
    topbarVisibilityChanged(state, action: PayloadAction<boolean>) {
      state.visible = action.payload;
    },
    topbarVisibilityToggled(state) {
      state.visible = !state.visible;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.fulfilled, (state, action) => {
        state.enabled = action.payload.settings.topbarEnabled;
      })
      .addCase(SDKConnected, (state, action) => {
        const options = action.payload;

        if (options.showTopbar !== undefined) {
          state.visible = options.showTopbar;
        }
      });
  },
});

export const { topbarVisibilityChanged, topbarVisibilityToggled } = topbarSlice.actions;

export const selectTopbarVisibility = createSelector(
  [
    (state: RootState) => state.topbar.visible,
    (state: RootState) => state.topbar.enabled,
    selectAllowChangeLogo,
  ],
  (visible, enabled, allowChangeLogo) => {
    const effectiveVisible = allowChangeLogo ? visible : true;
    return effectiveVisible && enabled;
  }
);

export const selectTopbarHeight = createSelector(
  [
    selectCurrentSystemRole,
    selectTopbarVisibility,
    selectRecorderLogoVisibility,
    selectRestreamerLogoVisibility,
  ],
  (systemRole, topBarVisible, recorderLogoEnabled, restreamerLogoEnabled) => {
    let systemLogoEnabled = false;
    if (systemRole === 'recorder') {
      systemLogoEnabled = recorderLogoEnabled;
    } else if (systemRole === 'restreamer') {
      systemLogoEnabled = restreamerLogoEnabled;
    }

    if (systemRole && systemLogoEnabled) {
      return styleConstants.topBar.height;
    }

    if (systemRole) {
      return 0;
    }

    if (!topBarVisible) {
      return 0;
    }

    return styleConstants.topBar.height;
  }
);

export default topbarSlice.reducer;
